import { skipToken } from "@reduxjs/toolkit/query";
import { useGetForumsQuery } from "messaging/_api/messaging-api";
import ForumQuery, { ForumFilterParameters, ForumOrderField, ForumOrderParameters } from "messaging/forums/_api/queries/forum-query";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import type { RecordDomain } from "permissions/_views/records/record-permissions-path";
import Guid from "_shared/guids/guid";
import { useMemo } from "react";
import { useSession } from "users/__session/session-context";
import { useGetProposalByIdQuery } from "work/proposals/_api/proposal-api";
import { useGetWorkAgreementByIdQuery } from "work/work-agreements/_api/work-agreement-api";

/**
 * A proposal's or a work agreement's own conversations and documents, for the group sections under
 * its record page's rows (#6172). The details browser widened a filter on one proposal or work
 * agreement to these same records; the record page lists each as its own group instead.
 *
 * Conversations are the forums Messaging keeps for the record (`Work.Proposal` or `Work.Agreement`
 * and its id). Documents are the policies and conflicts on the record's work agreement. Both are read
 * as the viewer: a record the viewer cannot read has no groups.
 */

export type RelatedRecords = Readonly<{
  forumIds: readonly string[];
  documentIds: readonly string[];
  isLoading: boolean;
  isError: boolean;
}>;

const NONE: RelatedRecords = { forumIds: [], documentIds: [], isLoading: false, isError: false };

/** The entity class Messaging files a domain's conversations under. */
const FORUM_ENTITY_CLASSES: Readonly<Partial<Record<RecordDomain, string>>> = {
  proposals: "Work.Proposal",
  "work-agreements": "Work.Agreement",
};

type PolicyDocumentsJSON = { documents?: readonly { documentId?: string | null }[] } | null | undefined;
type WorkDocumentsJSON = Readonly<{
  clientPolicies?: PolicyDocumentsJSON;
  vendorPolicies?: PolicyDocumentsJSON;
  conflicts?: PolicyDocumentsJSON;
}>;

/**
 * The ids of the documents a work agreement's JSON names: its client and vendor policies and its
 * conflicts. A masked list names none; an id is listed once.
 */
export function workDocumentIds(workAgreement: WorkDocumentsJSON | null | undefined): string[] {
  if (!workAgreement) return [];
  const ids = [workAgreement.clientPolicies, workAgreement.vendorPolicies, workAgreement.conflicts]
    .flatMap((list) => list?.documents ?? [])
    .flatMap((document) => (document.documentId && Guid.isValid(document.documentId) ? [document.documentId.toLowerCase()] : []));
  return [...new Set(ids)];
}

function forumsQuery(entityClass: string, recordId: string): ForumQuery {
  return new ForumQuery({
    filterParameters: new ForumFilterParameters({ entityClass: new ForumEntityClass(entityClass), entityId: new Guid(recordId) }),
    orderParameters: new ForumOrderParameters({ orderBy: ForumOrderField.Name }),
  });
}

export default function useRelatedRecords(domain: RecordDomain, recordId: string): RelatedRecords {
  const session = useSession();
  const entityClass = FORUM_ENTITY_CLASSES[domain];
  const readable = Boolean(entityClass && session.user && Guid.isValid(recordId));

  // Memoized on purpose: the query is a class instance that reaches RTK Query's arguments.
  const query = useMemo(() => (entityClass && Guid.isValid(recordId) ? forumsQuery(entityClass, recordId) : undefined), [entityClass, recordId]);
  const forums = useGetForumsQuery(readable && query ? { query } : skipToken);
  const proposal = useGetProposalByIdQuery(readable && domain === "proposals" ? { proposalId: recordId, session } : skipToken);
  const workAgreement = useGetWorkAgreementByIdQuery(
    readable && domain === "work-agreements" ? { workAgreementId: recordId, session } : skipToken,
  );

  const forumIds = useMemo(
    () => (forums.data ?? []).flatMap((forum) => (forum.id && Guid.isValid(forum.id) ? [forum.id.toLowerCase()] : [])),
    [forums.data],
  );
  // A proposal carries its work agreement's documents on itself.
  const work = domain === "proposals" ? proposal.data : workAgreement.data;
  const documentIds = useMemo(() => workDocumentIds(work as WorkDocumentsJSON | undefined), [work]);

  if (!readable) return NONE;
  const record = domain === "proposals" ? proposal : workAgreement;
  return {
    forumIds,
    documentIds,
    isLoading: Boolean(forums.isLoading || record.isLoading),
    isError: Boolean(forums.isError || record.isError),
  };
}
